/* HR — Odoo-style view primitives
 *
 * Shared building blocks that bring Odoo's most-useful UX patterns to our
 * editorial-style HR pages. Loaded before all module files.
 *
 * Exports (via window):
 *   <ViewSwitcher views value onChange />
 *   <FilterChips chips onRemove onAdd />
 *   <KanbanBoard columns renderCard />
 *   <CalendarMonth year month events onDateClick onEventClick onMonthChange />
 *   <ChatterPanel events onPostComment />
 *   <BulkActionBar selectedCount actions onClear />
 *   <StatusFlow steps current />
 *   <SmartButton icon label value onClick />
 *   <PivotTable rows cols cells rowLabel colLabel cellLabel />
 *   useRowSelection(items, idKey) → { selected, toggle, toggleAll, clear, isSelected, allSelected }
 */

/* ═════════════════════════════  ViewSwitcher  ═════════════════════════ */

function ViewSwitcher({ views, value, onChange }) {
  // Expressive: M3 segmented button (mockup .seg) — outline #848881, radius 10, active = secondary container.
  const c = GxT.color;
  return (
    <div style={uiStyle({
      display: 'inline-flex', gap: 0,
      border: `1px solid ${HR.rule}`, borderRadius: 4, overflow: 'hidden',
      background: HR.surface,
    }, {
      border: `1px solid ${c.ol}`, borderRadius: GxT.radius.btnSm, background: 'transparent',
    })}>
      {views.map(v => {
        const active = v.key === value;
        return (
          <button
            key={v.key}
            onClick={() => onChange(v.key)}
            title={v.label}
            aria-pressed={uiPick(undefined, active)}
            style={uiStyle({
              padding: '6px 12px', fontSize: FontSizes.xxs,
              background: active ? HR.ink : 'transparent',
              color: active ? '#fff' : HR.inkSoft,
              border: 'none', cursor: 'pointer',
              borderLeft: views[0].key === v.key ? 'none' : `1px solid ${HR.rule}`,
              fontFamily: HR.sans, fontWeight: active ? 500 : 400,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }, {
              minHeight: 32, padding: '0 14px', fontSize: 13, fontWeight: 700,
              background: active ? c.sc : 'transparent',
              color: active ? c.onSc : c.onV,
              borderLeft: views[0].key === v.key ? 'none' : `1px solid ${c.ol}`,
              justifyContent: 'center',
            })}
          >
            {v.icon && <Icon name={v.icon} size={11} stroke={2} />}
            <span>{v.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ═════════════════════════════  FilterChips  ══════════════════════════ */
/* Odoo-style: search box + chips below for active filters, with dropdown
 * to add new filter dimensions. Kept generic — caller supplies chip list
 * and dropdown content. */

function FilterChips({ search, onSearchChange, chips, onRemove, addOptions, onAdd, placeholder = '搜尋…' }) {
  const [adderOpen, setAdderOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setAdderOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const gc = GxT.color;
  return (
    <div style={uiStyle({
      padding: '14px 24px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
    }, {
      padding: '8px 20px 14px', borderBottom: 'none', background: 'transparent',
    })}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {/* Search box */}
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 380 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: HR.inkFaint, pointerEvents: 'none' }}>
            <Icon name="search" size={13} />
          </span>
          <input
            value={search ?? ''}
            placeholder={placeholder}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            style={uiStyle({
              width: '100%', padding: '8px 11px 8px 32px', fontSize: FontSizes.xs,
              border: `1px solid ${HR.rule}`, borderRadius: 4, background: HR.surface,
              outline: 'none', fontFamily: HR.sans, color: HR.ink,
            }, {
              /* outlined field (same recipe as the GX inputStyle primitive) */
              minHeight: 40, padding: '10px 12px 10px 32px', fontSize: 14,
              border: `1px solid ${gc.ol}`, borderRadius: GxT.radius.input,
              background: gc.card, color: gc.on,
            })}
          />
        </div>

        {/* Active chips */}
        {chips.map((c, i) => (
          <span key={i} style={uiStyle({
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 6px 5px 10px', fontSize: FontSizes.xxs,
            background: HR.accentSoft, color: HR.accent,
            border: `1px solid ${GxCore.alpha(HR.accent, '33')}`, borderRadius: 4,
            fontFamily: HR.sans, fontWeight: 500,
          }, {
            minHeight: 32, padding: '0 4px 0 12px', fontSize: 13, fontWeight: 700,
            background: gc.pc, color: gc.onPc, border: 'none', borderRadius: GxT.radius.chip,
          })}>
            <span style={uiStyle({ color: HR.inkFaint, fontWeight: 400 }, { color: gc.onV, fontWeight: 600 })}>{c.label}:</span>
            {c.value}
            <button
              onClick={() => onRemove(c.key)}
              aria-label="移除"
              style={uiStyle({
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: HR.accent, padding: '0 2px', lineHeight: 1, fontSize: FontSizes.sm,
              }, {
                color: gc.onPc, minWidth: 28, minHeight: 28, borderRadius: GxT.radius.chipSm, fontSize: 16,
              })}
            >×</button>
          </span>
        ))}

        {/* Add filter */}
        {addOptions && addOptions.length > 0 && (
          <div ref={wrapRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setAdderOpen(o => !o)}
              style={uiStyle({
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 10px', fontSize: FontSizes.xxs,
                background: HR.surface, color: HR.inkSoft,
                border: `1px dashed ${HR.rule}`, borderRadius: 4, cursor: 'pointer',
                fontFamily: HR.sans,
              }, {
                minHeight: 32, padding: '0 12px', fontSize: 13, fontWeight: 700,
                background: 'transparent', color: gc.onV,
                border: `1px dashed ${gc.ol}`, borderRadius: GxT.radius.chip,
              })}
            >
              <Icon name="plus" size={11} stroke={2} /> 新增篩選
            </button>
            {adderOpen && (
              <div style={uiStyle({
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
                background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 5,
                boxShadow: '0 8px 24px rgba(0,0,0,0.10)', minWidth: 160, padding: 4,
              }, {
                border: 'none', borderRadius: GxT.radius.inner, boxShadow: GxT.shadow.pop, padding: 6,
                maxHeight: 320, overflowY: 'auto',
              })}>
                {addOptions.map(o => (
                  <div
                    key={o.key}
                    onClick={() => { onAdd(o.key); setAdderOpen(false); }}
                    style={uiStyle({
                      padding: '8px 12px', fontSize: FontSizes.trading_md, cursor: 'pointer', borderRadius: 3,
                    }, {
                      padding: '9px 12px', fontSize: 13, borderRadius: GxT.radius.chip, whiteSpace: 'nowrap',
                    })}
                    onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {o.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════════  KanbanBoard  ══════════════════════════ */

function KanbanBoard({ columns, renderCard, emptyHint = '此欄無資料' }) {
  return (
    <div style={{
      display: 'flex', gap: 16, overflowX: 'auto', padding: '20px 24px',
      minHeight: 320,
    }}>
      {columns.map(col => (
        <div key={col.key} style={uiStyle({
          flex: '0 0 280px', display: 'flex', flexDirection: 'column',
          background: HR.surfaceAlt, borderRadius: 6, padding: 10,
          maxHeight: 600,
        }, { background: GxT.color.s2, borderRadius: GxT.radius.card })}>
          <div style={uiStyle({
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 6px 10px', borderBottom: `1px solid ${HR.rule}`, marginBottom: 8,
          }, { borderBottom: 'none', marginBottom: 4 })}>
            <div>
              <div style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: uiPick(500, 800), color: HR.ink }}>
                {col.label}
              </div>
              {col.subtitle && (
                <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, fontFamily: HR.mono, marginTop: 1 }}>
                  {col.subtitle}
                </div>
              )}
            </div>
            <span style={uiStyle({
              fontSize: FontSizes.trading_lg, padding: '2px 8px', borderRadius: 10,
              background: HR.surface, color: HR.inkSoft, fontFamily: HR.mono, fontWeight: 500,
            }, { borderRadius: GxT.radius.chip, fontFamily: GxT.font, fontWeight: 700, fontVariantNumeric: 'tabular-nums' })}>{col.items.length}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {col.items.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>
                {emptyHint}
              </div>
            ) : col.items.map(item => (
              <div
                key={item.id || item.key}
                style={uiStyle({
                  background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 5,
                  padding: 12, cursor: item.onClick ? 'pointer' : 'default',
                  transition: 'border-color 120ms, box-shadow 120ms',
                }, { border: '1px solid transparent', borderRadius: GxT.radius.inner })}
                onClick={item.onClick}
                onMouseEnter={(e) => { if (item.onClick) { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.06)'; } }}
                onMouseLeave={(e) => { if (item.onClick) { e.currentTarget.style.borderColor = HR.rule; e.currentTarget.style.boxShadow = 'none'; } }}
              >
                {renderCard(item)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═════════════════════════════  CalendarMonth  ════════════════════════ */
/* Renders a month grid. Events are positioned on dates, click handlers
 * supplied by caller. Weeks start on MONDAY (same as the attendance calendars).
 * On narrow screens (≤767px, matchMedia) the grid is compressed to 7 fitting
 * columns: each day shows its number plus status dots / "+N" instead of event
 * titles; tapping a day calls onDateClick (the caller shows that day's events). */

// Monday-first column headers (index 0 = Monday … 6 = Sunday).
const CAL_WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日'];

// Number of leading blank cells before day 1 in a Monday-first grid. month is 1-indexed.
function calMondayOffset(year, month) {
  return (new Date(year, month - 1, 1).getDay() + 6) % 7;
}

const CAL_NARROW_QUERY = '(max-width: 767px)';
// true when the viewport is ≤767px. Falls back to false (desktop layout) when
// matchMedia is unavailable (node tests, very old browsers).
function useCalNarrow() {
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(CAL_NARROW_QUERY).matches : false);
  const [narrow, setNarrow] = React.useState(get);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(CAL_NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on); };
  }, []);
  return narrow;
}

function CalendarMonth({ year, month, events = [], onDateClick, onEventClick, onMonthChange, today, selectedDate }) {
  // month is 1-indexed (1 = Jan)
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const daysInMonth = lastDay.getDate();
  // Start of grid: previous month's days to fill first week (Mon-first)
  const startWeekday = calMondayOffset(year, month); // 0 = Monday
  const compact = useCalNarrow();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startWeekday + 1;
    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push({ inMonth: false, date: null });
    } else {
      const ds = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
      cells.push({ inMonth: true, date: ds, day: dayNum });
    }
  }

  const eventsByDate = React.useMemo(() => {
    const m = {};
    events.forEach(ev => {
      // Spread events across from..to
      const from = ev.from_date || ev.date;
      const to = ev.to_date || ev.date || from;
      if (!from) return;
      const fromD = new Date(from); const toD = new Date(to);
      for (let d = new Date(fromD); d <= toD; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().slice(0, 10);
        (m[ds] = m[ds] || []).push(ev);
      }
    });
    return m;
  }, [events]);

  const monthLabels = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
  const goPrev = () => { const m = month === 1 ? 12 : month - 1; const y = month === 1 ? year - 1 : year; onMonthChange && onMonthChange(y, m); };
  const goNext = () => { const m = month === 12 ? 1 : month + 1; const y = month === 12 ? year + 1 : year; onMonthChange && onMonthChange(y, m); };
  const goToday = () => { const n = new Date(); onMonthChange && onMonthChange(n.getFullYear(), n.getMonth() + 1); };

  const gc = GxT.color;
  return (
    <div className="hr-cal-month">
      {/* Header bar */}
      <div style={uiStyle({
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', borderBottom: `1px solid ${HR.rule}`, background: HR.surfaceAlt,
      }, { padding: '16px 20px 12px', borderBottom: 'none', background: 'transparent', gap: 12 })}>
        <div style={{ display: 'flex', alignItems: 'center', gap: uiPick(10, 8) }}>
          <button onClick={goPrev} style={iconBtnStyle()} aria-label={uiPick(undefined, '上個月')}>←</button>
          <button onClick={goNext} style={iconBtnStyle()} aria-label={uiPick(undefined, '下個月')}>→</button>
          <button onClick={goToday} style={uiStyle({
            ...iconBtnStyle(),
            padding: '5px 12px', fontSize: FontSizes.xxs,
          }, { padding: '0 14px', fontSize: 13 })}>今日</button>
        </div>
        <div className="hr-cal-month-title" style={uiStyle({ fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: 500, letterSpacing: '-0.01em' }, { fontSize: GxT.size.h2, fontWeight: 900, letterSpacing: '-0.3px' })}>
          {year} 年 {monthLabels[month - 1]}
        </div>
        <div style={{ width: 90 }} />{/* spacer */}
      </div>

      <div className="hr-cal-month-scroll">
      {/* Weekday header */}
      <div style={calCompactPatch(compact, uiStyle({ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: `1px solid ${HR.rule}` }, { borderBottom: 'none', gap: 4, padding: '0 12px' }), uiPick({}, { gap: 3, padding: '0 6px' }))}>
        {CAL_WEEKDAY_LABELS.map((d, i) => (
          <div key={i} style={calCompactPatch(compact, uiStyle({
            padding: '10px 0', textAlign: 'center', fontSize: FontSizes.trading_lg,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: HR.inkFaint, fontWeight: 500,
            borderRight: i < 6 ? `1px solid ${HR.ruleSoft}` : 'none',
          }, { padding: '6px 0 8px', fontSize: 12, letterSpacing: 0, textTransform: 'none', color: gc.onV, fontWeight: 700, borderRight: 'none' }),
          { padding: '6px 0', fontSize: 12, letterSpacing: 0 })}>
            {compact ? null : '週'}{d}
          </div>
        ))}
      </div>

      {/* Cells — Expressive: tonal day tiles (radius 12) on a 4px gutter instead of hairline grid */}
      <div style={calCompactPatch(compact, uiStyle({ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }, { gap: 4, padding: '0 12px 12px' }), uiPick({}, { gap: 3, padding: '0 6px 8px' }))}>
        {cells.map((c, i) => {
          const dayEvents = c.date ? (eventsByDate[c.date] || []) : [];
          const isToday = c.date === (today || todayStr());
          const isLastRow = i >= cells.length - 7;
          const isLastCol = (i + 1) % 7 === 0;
          if (compact) {
            return (
              <CalCompactDay
                key={i} cell={c} dayEvents={dayEvents} isToday={isToday}
                isSelected={!!c.date && c.date === selectedDate}
                isLastRow={isLastRow} isLastCol={isLastCol} onDateClick={onDateClick}
              />
            );
          }
          return (
            <div
              key={i}
              onClick={() => c.date && onDateClick && onDateClick(c.date, dayEvents)}
              style={uiStyle({
                minHeight: 110, padding: 6,
                background: c.inMonth ? HR.surface : HR.surfaceAlt,
                borderRight: isLastCol ? 'none' : `1px solid ${HR.ruleSoft}`,
                borderBottom: isLastRow ? 'none' : `1px solid ${HR.ruleSoft}`,
                cursor: c.date && onDateClick ? 'pointer' : 'default',
                position: 'relative',
              }, {
                padding: 8, borderRight: 'none', borderBottom: 'none', borderRadius: GxT.radius.inner,
                background: c.inMonth ? (isToday ? gc.pc : gc.s1) : 'transparent',
                minWidth: 0,
              })}
            >
              {c.day && (
                <div style={uiStyle({
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: '50%',
                  fontSize: FontSizes.xxs, fontFamily: HR.mono, fontWeight: isToday ? 600 : 400,
                  color: isToday ? '#fff' : (c.inMonth ? HR.ink : HR.inkFaint),
                  background: isToday ? HR.accent : 'transparent',
                  marginBottom: 4,
                }, {
                  width: 26, height: 26, fontSize: 13, fontFamily: GxT.font,
                  fontWeight: isToday ? 800 : 600, fontVariantNumeric: 'tabular-nums',
                  color: isToday ? gc.onP : (c.inMonth ? gc.on : gc.faint),
                  background: isToday ? gc.p : 'transparent',
                })}>{c.day}</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: uiPick(2, 3) }}>
                {dayEvents.slice(0, 3).map((ev, idx) => (
                  <div
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(ev); }}
                    title={ev.title || ev.label}
                    style={uiStyle({
                      fontSize: FontSizes.trading_md, padding: '2px 6px', borderRadius: 3,
                      background: GxCore.alpha(ev.color || HR.accent, '20'), color: ev.color || HR.accent,
                      borderLeft: `2px solid ${ev.color || HR.accent}`,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      cursor: 'pointer', fontWeight: 500,
                    }, {
                      /* tonal event chip; callers may pass {bg, fg} tints (hr-calendar does under GX) */
                      padding: '3px 8px', borderRadius: GxT.radius.chipSm, borderLeft: 'none', fontWeight: 700,
                      background: ev.bg || (GxCore.alpha(ev.color || HR.accent, '20')),
                      color: ev.fg || ev.color || HR.accent,
                    })}
                  >
                    {ev.title || ev.label}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div style={{ fontSize: FontSizes.trading_sm, color: uiPick(HR.inkFaint, GxT.color.onV), padding: '0 4px' }}>+{dayEvents.length - 3} 更多</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}

// Narrow-screen style overrides. Returns `base` untouched when not compact so the
// desktop element tree is exactly what it was before the compact mode existed.
function calCompactPatch(compact, base, patch) {
  return compact ? Object.assign({}, base, patch) : base;
}

/* Narrow-screen day tile: date number + up to 3 status dots and a "+N" count.
 * The whole tile is the tap target; the caller lists the day's events. */
function CalCompactDay({ cell: c, dayEvents, isToday, isSelected, isLastRow, isLastCol, onDateClick }) {
  const gc = GxT.color;
  const clickable = !!(c.date && onDateClick);
  const pick = () => { if (clickable) onDateClick(c.date, dayEvents); };
  const dots = dayEvents.slice(0, 3);
  const more = dayEvents.length - dots.length;
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? isSelected : undefined}
      aria-label={c.date ? `${c.date}，${dayEvents.length} 個事件` : undefined}
      onClick={pick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }}
      style={uiStyle({
        minHeight: 52, padding: '4px 2px', minWidth: 0, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
        background: c.inMonth ? HR.surface : HR.surfaceAlt,
        borderRight: isLastCol ? 'none' : `1px solid ${HR.ruleSoft}`,
        borderBottom: isLastRow ? 'none' : `1px solid ${HR.ruleSoft}`,
        boxShadow: isSelected ? `inset 0 0 0 2px ${HR.accent}` : 'none',
        cursor: clickable ? 'pointer' : 'default',
      }, {
        padding: '4px 2px', borderRight: 'none', borderBottom: 'none', borderRadius: GxT.radius.inner,
        background: c.inMonth ? (isToday ? gc.pc : gc.s1) : 'transparent',
        boxShadow: isSelected ? `inset 0 0 0 2px ${gc.p}` : 'none',
      })}
    >
      {c.day && (
        <div style={uiStyle({
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%',
          fontSize: FontSizes.xxs, fontFamily: HR.mono, fontWeight: isToday ? 600 : 400,
          color: isToday ? '#fff' : (c.inMonth ? HR.ink : HR.inkFaint),
          background: isToday ? HR.accent : 'transparent',
        }, {
          width: 24, height: 24, fontSize: 12.5, fontFamily: GxT.font,
          fontWeight: isToday ? 800 : 600, fontVariantNumeric: 'tabular-nums',
          color: isToday ? gc.onP : (c.inMonth ? gc.on : gc.faint),
          background: isToday ? gc.p : 'transparent',
        })}>{c.day}</div>
      )}
      {dayEvents.length > 0 && (
        <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap', minHeight: 8 }}>
          {dots.map((ev, idx) => (
            <span key={idx} style={{ width: 6, height: 6, borderRadius: '50%', flex: '0 0 auto', background: ev.color || HR.accent }} />
          ))}
          {more > 0 && (
            <span style={{ fontSize: 10, lineHeight: 1, fontWeight: 600, color: uiPick(HR.inkFaint, gc.onV), fontVariantNumeric: 'tabular-nums' }}>+{more}</span>
          )}
        </div>
      )}
    </div>
  );
}

function iconBtnStyle() {
  return uiStyle({
    background: HR.surface, color: HR.inkSoft,
    border: `1px solid ${HR.rule}`, borderRadius: 3,
    padding: '5px 10px', fontSize: FontSizes.xxs, cursor: 'pointer',
    fontFamily: HR.mono,
  }, {
    /* tonal small button (mockup .btn.sm, 36px tall) */
    background: GxT.color.s2, color: GxT.color.onV, border: 'none', borderRadius: GxT.radius.btnSm,
    minWidth: 36, minHeight: 36, padding: '0 10px', fontSize: 15, fontWeight: 700, fontFamily: GxT.font,
  });
}

/* ═════════════════════════════  ChatterPanel  ═════════════════════════ */
/* Odoo's signature record-level activity log + comment thread.
 * events: [{ kind, who, when, body, before?, after?, field? }] */

function ChatterPanel({ events = [], onPostComment, placeholder = '寫下訊息或內部備註…' }) {
  const [draft, setDraft] = React.useState('');
  const post = () => {
    if (!draft.trim() || !onPostComment) return;
    onPostComment(draft.trim());
    setDraft('');
  };
  return (
    <div style={{ borderTop: `1px solid ${HR.rule}`, paddingTop: 18, marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon name="message" size={14} />
        <span style={{ fontFamily: HR.serif, fontSize: FontSizes.sm, fontWeight: uiPick(500, 800) }}>活動紀錄</span>
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>({events.length} 筆)</span>
      </div>

      {/* Comment composer */}
      {onPostComment && (
        <div style={uiStyle({
          display: 'flex', gap: 8, marginBottom: 16,
          padding: 10, background: HR.surfaceAlt, borderRadius: 5, border: `1px solid ${HR.rule}`,
        }, { background: GxT.color.card, borderRadius: GxT.radius.inner, border: `1px solid ${GxT.color.ol}` })}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            rows={2}
            style={{
              flex: 1, border: 'none', background: 'transparent', resize: 'none',
              outline: 'none', fontFamily: HR.sans, fontSize: FontSizes.xs, color: HR.ink,
            }}
          />
          <Button variant="primary" size="sm" onClick={post} disabled={!draft.trim()}>
            送出
          </Button>
        </div>
      )}

      {/* Timeline */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {events.length === 0 ? (
          <div style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal'), padding: 12 }}>
            尚無活動紀錄
          </div>
        ) : events.map((ev, i) => (
          <div key={i} style={{ display: 'flex', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
              background: ev.kind === 'comment' ? HR.accentSoft : HR.ruleSoft,
              color: ev.kind === 'comment' ? HR.accent : HR.inkSoft,
              display: 'grid', placeItems: 'center', fontSize: FontSizes.trading_lg, fontWeight: 600,
            }}>
              {ev.kind === 'comment'
                ? (ev.who || '?').slice(0, 1)
                : <Icon name={chatterIcon(ev.kind)} size={12} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: FontSizes.trading_md, color: HR.ink }}>
                <strong style={{ fontWeight: 500 }}>{ev.who || '系統'}</strong>
                {' '}<span style={{ color: HR.inkSoft, fontWeight: 400 }}>{chatterVerb(ev)}</span>
                {ev.field && <span style={{ color: HR.inkSoft }}> 「<span style={{ color: HR.ink }}>{ev.field}</span>」</span>}
              </div>
              {ev.kind === 'change' && (ev.before != null || ev.after != null) && (
                <div style={{ fontSize: FontSizes.trading_xl, color: HR.inkSoft, fontFamily: HR.mono, marginTop: 4 }}>
                  <span style={{ textDecoration: 'line-through', color: HR.inkFaint }}>{ev.before || '—'}</span>
                  {' → '}
                  <span style={{ color: HR.ink }}>{ev.after || '—'}</span>
                </div>
              )}
              {ev.kind === 'comment' && ev.body && (
                <div style={{
                  marginTop: 4, padding: '8px 12px',
                  background: HR.accentSoft, borderRadius: uiPick(5, GxT.radius.inner),
                  fontSize: FontSizes.xs, color: HR.ink, lineHeight: 1.5,
                  fontFamily: HR.serif,
                }}>{ev.body}</div>
              )}
              <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 3, fontFamily: HR.mono }}>
                {ev.when}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function chatterIcon(kind) {
  return { create: 'plus', change: 'refresh', delete: 'log-out', status: 'arrow-right', comment: 'message' }[kind] || 'more';
}
function chatterVerb(ev) {
  const k = ev.kind;
  if (k === 'create') return '建立了此筆紀錄';
  if (k === 'change') return '修改了';
  if (k === 'delete') return '刪除了此筆紀錄';
  if (k === 'status') return `將狀態改為 ${ev.after || ''}`;
  if (k === 'comment') return '留言：';
  return ev.label || '操作';
}

/* ═════════════════════════════  BulkActionBar  ════════════════════════ */
/* Sticky bar shown at the top of a list when rows are selected. */

function BulkActionBar({ selectedCount, actions, onClear }) {
  if (selectedCount <= 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 24px', background: HR.ink, color: '#fff',
      position: 'sticky', top: 0, zIndex: 5,
    }}>
      <span style={{ fontSize: FontSizes.xs, fontWeight: 500 }}>
        已選取 <span style={{ fontFamily: HR.mono }}>{selectedCount}</span> 筆
      </span>
      <button
        onClick={onClear}
        style={{
          background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
          color: '#fff', padding: '4px 10px', borderRadius: uiPick(3, GxT.radius.chip), fontSize: FontSizes.xxs, cursor: 'pointer',
        }}
      >清除</button>
      <div style={{ flex: 1 }} />
      {actions.map((a, i) => (
        <button
          key={i}
          onClick={a.onClick}
          disabled={a.disabled}
          style={{
            background: a.danger ? HR.down : 'rgba(255,255,255,0.12)',
            color: '#fff', border: 'none', padding: '6px 14px', borderRadius: uiPick(3, GxT.radius.btnSm),
            fontSize: FontSizes.trading_md, cursor: a.disabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            opacity: a.disabled ? 0.5 : 1, fontWeight: 500,
          }}
        >
          {a.icon && <Icon name={a.icon} size={12} stroke={2} />}
          {a.label}
        </button>
      ))}
    </div>
  );
}

/* ═════════════════════════════  StatusFlow  ═══════════════════════════ */
/* Horizontal pipeline: draft → submitted → approved → paid */

function StatusFlow({ steps, current }) {
  const idx = steps.findIndex(s => s.key === current);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '4px 0' }}>
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        const skipped = current === 'rejected' && i > idx;
        return (
          <React.Fragment key={s.key}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: uiPick(4, GxT.radius.chip),
              background: active ? HR.accent : (done ? HR.accentSoft : 'transparent'),
              color: active ? '#fff' : (done ? HR.accent : (skipped ? HR.inkFaint : HR.inkSoft)),
              fontWeight: active ? 500 : 400, fontSize: FontSizes.trading_md,
              opacity: skipped ? 0.4 : 1,
            }}>
              <span style={{
                width: 18, height: 18, borderRadius: '50%',
                background: active ? '#fff' : (done ? HR.accent : 'transparent'),
                color: active ? HR.accent : (done ? '#fff' : HR.inkFaint),
                border: !active && !done ? `1.5px solid ${HR.rule}` : 'none',
                display: 'grid', placeItems: 'center', fontSize: FontSizes.trading_sm, fontWeight: 600,
                fontFamily: HR.mono,
              }}>{done ? '✓' : i + 1}</span>
              {s.label}
            </div>
            {i < steps.length - 1 && (
              <div style={{
                width: 24, height: 1, background: done ? HR.accent : HR.rule,
                margin: '0 -2px',
              }} />
            )}
          </React.Fragment>
        );
      })}
      {current === 'rejected' && (
        <div style={{ marginLeft: 16, padding: '6px 12px', background: HR.down, color: '#fff', borderRadius: uiPick(4, GxT.radius.chip), fontSize: FontSizes.trading_md, fontWeight: uiPick(500, 700) }}>
          已退回
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════  SmartButton  ══════════════════════════ */
/* Odoo's smart button: large clickable card with icon + value + label.
 * Used on detail pages to surface related counts ("5 Time Off requests"). */

function SmartButton({ icon, value, label, onClick, accent, mock }) {
  return (
    <button
      onClick={onClick}
      style={uiStyle({
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
        padding: '12px 16px', minWidth: 110,
        background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 5,
        cursor: onClick ? 'pointer' : 'default', position: 'relative',
        transition: 'border-color 120ms', fontFamily: HR.sans, color: HR.ink,
        textAlign: 'left',
      }, { background: GxT.color.s1, borderRadius: GxT.radius.inner })}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.borderColor = HR.accent; }}
      onMouseLeave={(e) => { if (onClick) e.currentTarget.style.borderColor = HR.rule; }}
    >
      <span style={uiStyle({
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: FontSizes.trading_lg, color: HR.inkFaint,
        letterSpacing: '0.06em', textTransform: 'uppercase',
      }, { fontSize: 12, fontWeight: 700, color: GxT.color.onV, letterSpacing: 0, textTransform: 'none' })}>
        {icon && <Icon name={icon} size={11} />}
        {label}
      </span>
      <span style={{
        fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: uiPick(500, 800), lineHeight: 1,
        color: accent ? HR.accent : HR.ink, fontVariantNumeric: 'tabular-nums',
      }}>{value}</span>
      {mock && (
        <span style={{
          position: 'absolute', top: 6, right: 6,
          fontSize: FontSizes.trading_3xs, padding: '1px 4px', borderRadius: 2,
          background: HR.warn, color: '#fff', fontWeight: 600,
          letterSpacing: '0.05em', fontFamily: HR.mono,
        }}>MOCK</span>
      )}
    </button>
  );
}

/* ═════════════════════════════  PivotTable  ═══════════════════════════ */
/* Generic 2D matrix. rows / cols are arrays of { key, label }. cells is
 * { rowKey: { colKey: value } } shape. */

function PivotTable({ rows, cols, cells, rowHeader = '項目', renderCell, rowTotals = true, colTotals = true }) {
  const sumRow = (rk) => cols.reduce((s, c) => s + (Number((cells[rk] || {})[c.key]) || 0), 0);
  const sumCol = (ck) => rows.reduce((s, r) => s + (Number((cells[r.key] || {})[ck]) || 0), 0);
  const grandTotal = rows.reduce((s, r) => s + sumRow(r.key), 0);

  // Color scale 0..max → opacity 0.05..0.85
  const allValues = [];
  rows.forEach(r => cols.forEach(c => { const v = Number((cells[r.key] || {})[c.key]) || 0; if (v > 0) allValues.push(v); }));
  const maxV = Math.max(1, ...allValues);

  const fmt = renderCell || ((v) => v ? String(v) : '');

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.trading_md, minWidth: 600 }}>
        <thead>
          <tr style={uiStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkFaint }, { letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontSize: 12 })}>
            <th style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 500, position: 'sticky', left: 0, background: HR.surfaceAlt, zIndex: 1 }}>
              {rowHeader}
            </th>
            {cols.map(c => (
              <th key={c.key} style={{ padding: '11px 12px', textAlign: 'right', fontWeight: 500, fontFamily: HR.mono }}>
                {c.label}
              </th>
            ))}
            {rowTotals && (
              <th style={{ padding: '11px 14px', textAlign: 'right', fontWeight: 500, background: HR.ruleSoft }}>
                合計
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
              <td style={{
                padding: '11px 14px', fontWeight: 500, color: HR.ink,
                position: 'sticky', left: 0, background: HR.surface, zIndex: 1,
              }}>{r.label}</td>
              {cols.map(c => {
                const v = (cells[r.key] || {})[c.key];
                const num = Number(v) || 0;
                const intensity = num > 0 ? Math.min(0.85, 0.05 + (num / maxV) * 0.55) : 0;
                return (
                  <td key={c.key} style={{
                    padding: '11px 12px', textAlign: 'right',
                    fontFamily: HR.mono, fontVariantNumeric: 'tabular-nums',
                    background: intensity > 0 ? `rgba(11, 95, 90, ${intensity})` : 'transparent',
                    color: intensity > 0.4 ? '#fff' : HR.ink,
                    fontWeight: num > 0 ? 500 : 400,
                  }}>{v == null ? '' : fmt(v, r, c)}</td>
                );
              })}
              {rowTotals && (
                <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600, background: HR.surfaceAlt }}>
                  {fmt(sumRow(r.key))}
                </td>
              )}
            </tr>
          ))}
          {colTotals && (
            <tr style={{ borderTop: `2px solid ${HR.rule}`, background: HR.surfaceAlt }}>
              <td style={uiStyle({
                padding: '11px 14px', fontWeight: 600, fontSize: FontSizes.trading_lg,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: HR.inkSoft,
                position: 'sticky', left: 0, background: HR.surfaceAlt, zIndex: 1,
              }, { letterSpacing: 0, textTransform: 'none', fontWeight: 700, fontSize: 12 })}>合計</td>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '11px 12px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>
                  {fmt(sumCol(c.key))}
                </td>
              ))}
              {rowTotals && (
                <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 700, background: HR.ink, color: '#fff' }}>
                  {fmt(grandTotal)}
                </td>
              )}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ═════════════════════════════  Row selection hook  ═══════════════════ */

function useRowSelection(items, idKey = 'id') {
  const [selected, setSelected] = React.useState(new Set());
  // Reset selection when items list changes substantially
  const itemIds = items.map(x => x[idKey]).join(',');
  React.useEffect(() => {
    setSelected(prev => {
      const validIds = new Set(items.map(x => x[idKey]));
      const next = new Set();
      prev.forEach(id => { if (validIds.has(id)) next.add(id); });
      return next;
    });
  }, [itemIds]);

  const isSelected = (id) => selected.has(id);
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(prev => {
    if (prev.size === items.length) return new Set();
    return new Set(items.map(x => x[idKey]));
  });
  const clear = () => setSelected(new Set());
  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && selected.size < items.length;

  return { selected, isSelected, toggle, toggleAll, clear, allSelected, someSelected, count: selected.size };
}

/* ═════════════════════════════  Selection checkbox cell  ══════════════ */

function SelectCell({ checked, indeterminate, onChange, header }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={!!checked}
      onChange={(e) => { e.stopPropagation(); onChange && onChange(); }}
      onClick={(e) => e.stopPropagation()}
      style={{ width: 14, height: 14, accentColor: header ? HR.ink : HR.accent, cursor: 'pointer' }}
    />
  );
}

/* ═════════════════════════════  Exports  ══════════════════════════════ */

Object.assign(window, {
  ViewSwitcher, FilterChips, KanbanBoard,
  CalendarMonth, ChatterPanel,
  BulkActionBar, StatusFlow, SmartButton, PivotTable,
  useRowSelection, SelectCell,
});
